import React, { useEffect, useState } from "react";
import { Drawer, Button, Space, message, Divider, Empty, Tag } from "antd";

import api from "../../../../api";
import CustomSelect from "../../../../components/select/CustomSelect";
import CustomDatePicker from "../../../../components/picker/CustomDatePicker";
import CustomInput from "../../../../components/input/CustomInput";
import CustomTimePicker from "../../../../components/picker/CustomTimePicker";
import styles from "../styles.module.scss";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useToast } from "../../../../components/toast";
dayjs.extend(isSameOrBefore);

const WEEKDAY_LABELS = ["Да", "Мя", "Лх", "Пү", "Ба", "Бя", "Ня"];
const LABEL_TO_DAY = { Да: 1, Мя: 2, Лх: 3, Пү: 4, Ба: 5, Бя: 6, Ня: 0 };

export default function ActionDrawer({ open, onClose, classes, trigger }) {
  // base
  const [classId, setClassId] = useState(null);

  // dates + weekdays
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);

  // times
  const [startTime, setStartTime] = useState(null); // "HH:mm"
  const [endTime, setEndTime] = useState(null); // "HH:mm"
  const [durationMin, setDurationMin] = useState(60);
  const [breakMin, setBreakMin] = useState(0);

  // misc
  const [capacity, setCapacity] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // preview
  const [previewList, setPreviewList] = useState([]); // бүх мөр
  const [excludedKeys, setExcludedKeys] = useState(new Set()); // key: "YYYY-MM-DD|HH:mm|HH:mm"

  /** ---------------------- Effects ---------------------- */
  const toast = useToast();

  // Preview-ийг тооцоолж шинэчлэх
  useEffect(() => {
    try {
      if (!classId || !startDate || !endDate) {
        setPreviewList([]);
        return;
      }
      if (!selectedWeekdays.length || !startTime || !endTime) {
        setPreviewList([]);
        return;
      }
      if (Number(durationMin) <= 0) {
        setPreviewList([]);
        return;
      }

      let s = startDate;
      let e = endDate;
      if (dayjs(e).isBefore(s)) [s, e] = [e, s];

      const allDates = enumerateDates(s, e);
      const weekdayNums = selectedWeekdays.map((lb) => LABEL_TO_DAY[lb]);
      const filteredDates = allDates.filter((d) =>
        weekdayNums.includes(d.day())
      );

      const startHH =
        typeof startTime === "string"
          ? startTime
          : dayjs(startTime).format("HH:mm");
      const endHH =
        typeof endTime === "string" ? endTime : dayjs(endTime).format("HH:mm");

      const slots = buildTimeSlots(
        startHH,
        endHH,
        Number(durationMin),
        Number(breakMin || 0)
      );

      const rows = [];
      for (const d of filteredDates) {
        for (const s of slots) {
          rows.push({
            dateStr: d.format("YYYY-MM-DD"),
            startStr: s.start,
            endStr: s.end,
          });
        }
      }
      setPreviewList(rows);
    } catch {
      setPreviewList([]);
    }
  }, [
    classId,
    startDate,
    endDate,
    selectedWeekdays,
    startTime,
    endTime,
    durationMin,
    breakMin,
  ]);

  // Preview хоосорвол excludedKeys-ийг цэвэрлэх
  useEffect(() => {
    if (!previewList.length && excludedKeys.size) {
      setExcludedKeys(new Set());
    }
  }, [previewList, excludedKeys]);

  /** ---------------------- Derived ---------------------- */

  const keyOf = (r) => `${r.dateStr}|${r.startStr}|${r.endStr}`;

  const visibleSlots = previewList.filter((r) => !excludedKeys.has(keyOf(r)));

  // Өдрөөр бүлэглэх
  const groupedByDate = visibleSlots.reduce((acc, r) => {
    if (!acc[r.dateStr]) acc[r.dateStr] = [];
    acc[r.dateStr].push({ start: r.startStr, end: r.endStr });
    return acc;
  }, {});

  // өдөр дотор цагийг эрэмбэлэх
  Object.values(groupedByDate).forEach((arr) =>
    arr.sort((a, b) => toMinOfDay(a.start) - toMinOfDay(b.start))
  );

  /** ---------------------- Handlers ---------------------- */

  const toggleWeekday = (label) => {
    setSelectedWeekdays((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  const toggleSlot = (dateStr, start, end) => {
    const key = `${dateStr}|${start}|${end}`;
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetAll = () => {
    setClassId(null);

    setStartDate(null);
    setEndDate(null);
    setSelectedWeekdays([]);

    setStartTime(null);
    setEndTime(null);
    setDurationMin(60);
    setBreakMin(0);

    setCapacity(0);

    setPreviewList([]);
    setExcludedKeys(new Set());
  };

  const canSubmit =
    classId &&
    startDate &&
    endDate &&
    selectedWeekdays.length > 0 &&
    startTime &&
    endTime &&
    Number(durationMin) > 0 &&
    Number(capacity) > 0 &&
    visibleSlots.length > 0;

  const makeIsoLocal = (dateStr, hhmm) =>
    dayjs(`${dateStr} ${hhmm}`, "YYYY-MM-DD HH:mm").toDate().toISOString();

  const submit = async () => {
    if (!canSubmit) {
      return message.warning("Бүх шаардлагатай талбараа бөглөнө үү.");
    }

    const slots = visibleSlots.map((r) => ({
      starts_at: makeIsoLocal(r.dateStr, r.startStr),
      ends_at: makeIsoLocal(r.dateStr, r.endStr),
    }));

    const payload = {
      class_id: classId,
      slots,
      capacity: Number(capacity),
    };

    try {
      setSubmitting(true);
      const res = await api.create_schedules_bulk(payload);
      const { created, skipped_duplicates } = res?.data?.data || {};
      toast(
        `Нийт: +${created || 0}, давхардал: ${skipped_duplicates || 0}`,
        "success"
      );
      trigger();
      onClose?.(true);
      resetAll();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "Алдаа гарлаа");
    } finally {
      setSubmitting(false);
    }
  };
  /** ---------------------- Render ---------------------- */

  return (
    <Drawer
      title="Хуваарь үүсгэх"
      open={open}
      onClose={() => onClose?.(false)}
      width={720}
      styles={{ body: { paddingBottom: 16 } }}
      extra={
        <Space>
          <Button onClick={() => onClose?.(false)}>Болих</Button>
          <Button
            type="primary"
            onClick={submit}
            disabled={!canSubmit}
            loading={submitting}
          >
            Үүсгэх
          </Button>
        </Space>
      }
    >
      <div className="d-flex flex-column gap-3">
        {/* Class + Capacity */}
        <div className={`${styles.row} ${styles.gridClassCapPub}`}>
          <CustomSelect
            allowClear
            showSearch
            required
            title="Өрөө (Class)"
            value={classId}
            onChange={setClassId}
            placeholder="Өрөө сонгох"
            options={classes.map((c) => ({
              value: c.id,
              label: `${c.title} (${c.program})`,
            }))}
            fullWidth
          />
          <CustomInput
            required
            title="Орон тоо"
            value={capacity}
            onChange={(e) => setCapacity(Number(e?.target?.value ?? e))}
            type="number"
            fullWidth
          />
        </div>

        {/* Dates */}
        <Divider
          orientation="left"
          style={{ borderColor: "#ddd", fontSize: 14, fontWeight: 600 }}
        >
          Өдрүүд
        </Divider>
        <div className={`${styles.row} ${styles.grid2}`}>
          <CustomDatePicker
            required
            title="Эхлэх өдөр"
            value={startDate}
            onChange={setStartDate}
            fullWidth
          />
          <CustomDatePicker
            required
            title="Дуусах өдөр"
            value={endDate}
            onChange={setEndDate}
            fullWidth
          />
        </div>

        {/* Weekdays */}
        <div className={styles.weekdays}>
          {WEEKDAY_LABELS.map((lb) => {
            const active = selectedWeekdays.includes(lb);
            return (
              <div
                key={lb}
                className={`${styles.day} ${active ? styles.dayActive : ""}`}
                onClick={() => toggleWeekday(lb)}
              >
                {lb}
              </div>
            );
          })}
        </div>

        {/* Times */}
        <Divider
          orientation="left"
          style={{ borderColor: "#ddd", fontSize: 14, fontWeight: 600 }}
        >
          Цагууд
        </Divider>
        <div className={`${styles.row} ${styles.grid2}`}>
          <CustomTimePicker
            required
            title="Эхлэх цаг"
            value={startTime ? dayjs(startTime, "HH:mm") : null}
            onChange={(v) => setStartTime(v ? dayjs(v).format("HH:mm") : null)}
            showSecond={false}
            fullWidth
          />
          <CustomTimePicker
            required
            title="Дуусах цаг"
            value={endTime ? dayjs(endTime, "HH:mm") : null}
            onChange={(v) => setEndTime(v ? dayjs(v).format("HH:mm") : null)}
            showSecond={false}
            fullWidth
          />
        </div>
        <div className={`${styles.row} ${styles.grid2}`}>
          <CustomInput
            required
            title="Үргэлжлэх минут"
            value={durationMin}
            onChange={(e) => setDurationMin(Number(e?.target?.value ?? e))}
            type="number"
            fullWidth
          />
          <CustomInput
            title="Завсарлах минут"
            value={breakMin}
            onChange={(e) => setBreakMin(Number(e?.target?.value ?? e))}
            type="number"
            fullWidth
          />
        </div>

        {/* Grouped preview with toggle remove */}
        <Divider
          orientation="left"
          style={{ borderColor: "#ddd", fontSize: 14, fontWeight: 600 }}
        >
          Үүсэх хуваарь
        </Divider>
        {Object.keys(groupedByDate).length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {Object.entries(groupedByDate).map(([date, slots]) => (
              <div key={date} className="d-flex flex-row gap-2">
                <div className={styles.dayWrapper}>{date}</div>
                <div className={styles.slotList}>
                  {slots.map((s) => {
                    const key = `${date}|${s.start}|${s.end}`;
                    const isExcluded = excludedKeys.has(key);
                    return (
                      <Tag
                        key={key}
                        className={styles.slotTag}
                        color={isExcluded ? "default" : "blue"}
                      >
                        <span className={styles.slotTime}>
                          {s.start} → {s.end}
                        </span>
                        <button
                          type="button"
                          aria-label="toggle"
                          className={styles.removeBtn}
                          onClick={() => toggleSlot(date, s.start, s.end)}
                          title={isExcluded ? "Буцаах" : "Устгах"}
                        >
                          {isExcluded ? "+" : "×"}
                        </button>
                      </Tag>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
    </Drawer>
  );
}

/* ---------------- Helpers ---------------- */
function enumerateDates(start, end) {
  const out = [];
  if (!start || !end) return out;
  let cur = dayjs(start).startOf("day");
  const last = dayjs(end).startOf("day");
  while (cur.isSameOrBefore(last)) {
    out.push(cur);
    cur = cur.add(1, "day");
  }
  return out;
}
function toMinOfDay(hhmm) {
  if (hhmm && typeof hhmm !== "string") {
    try {
      hhmm = dayjs(hhmm).format("HH:mm");
    } catch {}
  }
  const [h, m] = String(hhmm)
    .split(":")
    .map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}
function pad2(n) {
  return String(n).padStart(2, "0");
}
function asHHMM(minOfDay) {
  const h = Math.floor(minOfDay / 60);
  const m = minOfDay % 60;
  return `${pad2(h)}:${pad2(m)}`;
}
function buildTimeSlots(startHHMM, endHHMM, durationMin, breakMin = 0) {
  const slots = [];
  const startM = toMinOfDay(startHHMM);
  const endM = toMinOfDay(endHHMM);
  const d = Math.max(0, Number(durationMin));
  const b = Math.max(0, Number(breakMin || 0));
  const step = d + b;
  if (endM <= startM || d <= 0 || step <= 0) return slots;
  for (let t = startM; t + d <= endM; t += step) {
    const e = t + d;
    slots.push({ start: asHHMM(t), end: asHHMM(e) });
  }
  return slots;
}
